import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQC } from './QCContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus,
  Briefcase,
  Clock,
  CheckCircle2,
  Users,
  Play,
  Pause,
  AlertTriangle,
  Upload,
  FileSpreadsheet,
  Download,
  Eye,
  Settings,
  BarChart3,
  CreditCard,
  FileText,
} from 'lucide-react';
import { QCOvertimeList } from './QCOvertimeList';

export const QCSupervisorDashboard = () => {
  const { user, api, emergencyMode, toggleEmergencyMode } = useQC();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    total_jobs: 0,
    open_jobs: 0,
    in_progress: 0,
    filled: 0,
    total_employees: 0,
  });
  const [jobs, setJobs] = useState([]);
  const [canvassWindows, setCanvassWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create job dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    start_date: '',
    end_date: '',
    description: '',
    work_order_id: '',
    crew_leader_count: 1,
    mechanic_count: 1,
    crew_member_count: 3,
  });
  const [creating, setCreating] = useState(false);

  // Overtime upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [applyingPdf, setApplyingPdf] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Fetch canvass jobs
      const jobsRes = await api.get('/canvass/jobs').catch(() => ({ data: [] }));
      setJobs(jobsRes.data || []);
      
      // Fetch canvass windows
      const windowsRes = await api.get('/canvass/windows').catch(() => ({ data: [] }));
      setCanvassWindows(windowsRes.data || []);

      // Fetch bid order for employee count
      const bidOrderRes = await api.get('/canvass/bid-order').catch(() => ({ data: [] }));
      
      const jobsList = jobsRes.data || [];
      setStats({
        total_jobs: jobsList.length,
        open_jobs: jobsList.filter(j => j.status === 'open').length,
        in_progress: jobsList.filter(j => j.status === 'in_progress').length,
        filled: jobsList.filter(j => j.status === 'filled' || j.status === 'completed').length,
        total_employees: (bidOrderRes.data || []).length,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleCreateJob = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.location) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      await api.post('/canvass/jobs', {
        title: formData.title,
        location: formData.location,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        description: formData.description || undefined,
        work_order_id: formData.work_order_id || undefined,
        crew_requirements: {
          crew_leader: parseInt(formData.crew_leader_count) || 1,
          mechanic: parseInt(formData.mechanic_count) || 1,
          crew_member: parseInt(formData.crew_member_count) || 3,
        },
      });
      toast.success('Job created successfully!');
      setCreateDialogOpen(false);
      setFormData({
        title: '',
        location: '',
        start_date: '',
        end_date: '',
        description: '',
        work_order_id: '',
        crew_leader_count: 1,
        mechanic_count: 1,
        crew_member_count: 3,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setCreating(false);
    }
  };

  const handleStartCanvass = async (jobId) => {
    try {
      // Get job details for the window name
      const job = jobs.find(j => j.id === jobId);
      const jobTitle = job?.title || 'Job';
      
      // Create a new canvass window for this job
      await api.post('/canvass/windows', {
        name: `Canvass - ${jobTitle}`,
        mode: 'DRAFT',
      });
      
      // Add the job to the window
      const windowsRes = await api.get('/canvass/windows');
      const activeWindow = windowsRes.data?.find(w => w.status === 'active' || w.status === 'open');
      
      if (activeWindow) {
        await api.post(`/canvass/windows/${activeWindow.id}/add-jobs`, {
          job_ids: [jobId]
        });
      }
      
      toast.success('Canvassing started!');
      fetchData();
      navigate('/canvass');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start canvassing');
    }
  };

  const handleOvertimeUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    const isPdf = uploadFile.name.toLowerCase().endsWith('.pdf');
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      if (isPdf) {
        // Use PDF parsing endpoint
        const response = await api.post('/admin/overtime/upload-pdf', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        // Show preview instead of applying immediately
        setPdfPreview({
          snapshot_id: response.data.snapshot_id,
          employees: response.data.employees || [],
          extracted_count: response.data.extracted_count,
        });
        toast.success(`Extracted ${response.data.extracted_count} employees from PDF. Review and apply.`);
      } else {
        // CSV upload - apply directly
        await api.post('/admin/overtime/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Overtime data uploaded successfully!');
        setUploadDialogOpen(false);
        setUploadFile(null);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload overtime data');
    } finally {
      setUploading(false);
    }
  };

  const handleApplyPdfData = async () => {
    if (!pdfPreview?.snapshot_id) return;
    
    setApplyingPdf(true);
    try {
      const response = await api.post(`/admin/overtime/apply-pdf/${pdfPreview.snapshot_id}`);
      toast.success(`Applied ${response.data.applied} records! ${response.data.failed} could not be matched.`);
      setUploadDialogOpen(false);
      setUploadFile(null);
      setPdfPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply PDF data');
    } finally {
      setApplyingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="qc-supervisor-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight">
            Supervisor Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">Manage overtime jobs and canvassing</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Quick Links */}
          <Button variant="outline" onClick={() => navigate('/quickcanvass/staffing-requests')} className="gap-2">
            <FileText className="h-4 w-4" />
            Staffing Requests
          </Button>
          <Button variant="outline" onClick={() => navigate('/quickcanvass/reports')} className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Reports
          </Button>
          <Button variant="outline" onClick={() => navigate('/quickcanvass/settings')} className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </Button>
          <Button variant="outline" onClick={() => navigate('/quickcanvass/subscription')} className="gap-2">
            <CreditCard className="h-4 w-4" />
            Subscription
          </Button>
          
          {/* Emergency Mode Toggle */}
          <Button
            variant={emergencyMode ? 'destructive' : 'outline'}
            onClick={toggleEmergencyMode}
            className="gap-2"
          >
            <AlertTriangle className="h-4 w-4" />
            {emergencyMode ? 'Emergency ON' : 'Emergency'}
          </Button>

          {/* Upload Overtime */}
          <Dialog open={uploadDialogOpen} onOpenChange={(open) => {
            setUploadDialogOpen(open);
            if (!open) {
              setUploadFile(null);
              setPdfPreview(null);
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Upload className="h-4 w-4" />
                Upload Overtime
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Upload Overtime Data
                </DialogTitle>
              </DialogHeader>
              
              {!pdfPreview ? (
                <div className="py-4 space-y-4">
                  <div>
                    <Label>CSV or PDF File (ARCOS format supported)</Label>
                    <Input
                      type="file"
                      accept=".csv,.pdf"
                      onChange={(e) => setUploadFile(e.target.files?.[0])}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      <strong>CSV:</strong> email, overtime_hours<br/>
                      <strong>PDF:</strong> ARCOS Callout Lists (AI-powered extraction)
                    </p>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleOvertimeUpload} disabled={uploading || !uploadFile}>
                      {uploading ? 'Processing...' : uploadFile?.name?.endsWith('.pdf') ? 'Extract from PDF' : 'Upload CSV'}
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                <div className="py-4 space-y-4">
                  <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <h4 className="font-bold text-green-800 dark:text-green-200">
                      Extracted {pdfPreview.extracted_count} Employees
                    </h4>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Review the data below and click "Apply" to update overtime records.
                    </p>
                  </div>
                  
                  <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Class</TableHead>
                          <TableHead>OT Hours</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pdfPreview.employees.map((emp, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{emp.rank || idx + 1}</TableCell>
                            <TableCell className="font-medium">{emp.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {emp.employee_class || 'N/A'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono">{emp.overtime_hours?.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPdfPreview(null)}>
                      Back
                    </Button>
                    <Button onClick={handleApplyPdfData} disabled={applyingPdf}>
                      {applyingPdf ? 'Applying...' : 'Apply to System'}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Create Job */}
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="h-5 w-5" />
                Create Job
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New Job</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateJob} className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Job Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Valve Maintenance"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Location *</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., Station 42"
                      required
                    />
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Work Order ID</Label>
                    <Input
                      value={formData.work_order_id}
                      onChange={(e) => setFormData({ ...formData, work_order_id: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="mb-2 block">Crew Requirements</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Crew Leaders</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.crew_leader_count}
                        onChange={(e) => setFormData({ ...formData, crew_leader_count: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Mechanics</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.mechanic_count}
                        onChange={(e) => setFormData({ ...formData, mechanic_count: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Crew Members</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.crew_member_count}
                        onChange={(e) => setFormData({ ...formData, crew_member_count: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Job details..."
                    rows={3}
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating}>
                    {creating ? 'Creating...' : 'Create Job'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="stats-grid">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.total_jobs}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Open</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.open_jobs}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.in_progress}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Filled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.filled}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Employees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.total_employees}</div>
          </CardContent>
        </Card>
      </div>

      {/* Overtime List */}
      <QCOvertimeList currentUserId={user?.id} />

      {/* Active Canvass Windows */}
      {canvassWindows.filter(w => w.status === 'active' || w.status === 'open').length > 0 && (
        <Card className="border-2 border-primary">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-primary" />
              Active Canvass Windows
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {canvassWindows.filter(w => w.status === 'active' || w.status === 'open').map((window) => (
                <div key={window.id} className="flex items-center justify-between p-4 border rounded-lg bg-primary/5">
                  <div>
                    <h4 className="font-semibold">{window.title}</h4>
                    <p className="text-sm text-muted-foreground">
                      Mode: {window.mode} | Jobs: {window.job_ids?.length || 0}
                    </p>
                  </div>
                  <Button onClick={() => navigate('/canvass')} className="gap-2">
                    <Eye className="h-4 w-4" />
                    View Draft Board
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Jobs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Jobs
              </CardTitle>
              <CardDescription>Manage overtime jobs and canvassing</CardDescription>
            </div>
            <Button variant="outline" onClick={() => navigate('/canvass')} className="gap-2">
              <Eye className="h-4 w-4" />
              Go to Draft Board
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No jobs created yet</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="mt-4">
                Create First Job
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job Title</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Crew</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-medium">{job.title}</TableCell>
                    <TableCell>{job.location}</TableCell>
                    <TableCell>{job.start_date || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {job.crew_status && Object.entries(job.crew_status).map(([role, status]) => (
                          <Badge key={role} variant="outline" className="text-xs">
                            {role.charAt(0)}: {status.filled}/{status.required}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        job.status === 'open' ? 'default' :
                        job.status === 'in_progress' ? 'secondary' :
                        job.status === 'filled' ? 'outline' : 'destructive'
                      }>
                        {job.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {job.status === 'open' && (
                          <Button size="sm" onClick={() => handleStartCanvass(job.id)} className="gap-1">
                            <Play className="h-3 w-3" />
                            Start Canvass
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => navigate(`/canvass`)}>
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QCSupervisorDashboard;
